/**
 * /app/{tenantSlug}/settings/roles — Settings → custom / granular RBAC.
 *
 * Batch 32 Track B. HR-admin (or anyone holding role.admin) defines custom
 * roles and assigns capabilities via a module × action matrix. Custom roles
 * AUGMENT the fixed role — they only ADD capabilities. A `dangerous` token
 * (flagged) requires a step-up 2FA code to assign.
 */

import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  Loader2,
  PlusCircle,
  ShieldAlert,
  Trash2,
  UserMinus,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { toApiError } from "@/lib/api";
import {
  useAssignCustomRole,
  useCapabilityCatalog,
  useCreateCustomRole,
  useCustomRoles,
  useDeleteCustomRole,
  useEffectiveCapabilities,
  useRoleMembers,
  useUnassignCustomRole,
  useUpdateCustomRole,
} from "@/hooks/use-custom-rbac";
import { capabilityModuleLabel } from "@/lib/capability-modules";
import type { Capability, CustomRole } from "@/lib/api/custom-roles";
import { EmployeeDirectoryPicker } from "@/components/shared/employee-directory-picker";
import { SettingsPageHeader } from "@/components/shared/settings-page-header";
import { SettingsShell } from "@/components/shared/settings-shell";
import { SETTINGS_GROUPS } from "@/lib/settings-nav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/settings/roles",
)({
  component: SettingsRolesRoute,
});

// ---------------------------------------------------------------------------
// Role editor dialog (create + edit share the matrix builder)
// ---------------------------------------------------------------------------

interface RoleEditorProps {
  catalog: Capability[];
  /** Tokens the CURRENT user effectively holds — others are non-grantable. */
  myCaps: string[];
  bypass: boolean;
  existing?: CustomRole;
  trigger: React.ReactNode;
}

function RoleEditor({ catalog, myCaps, bypass, existing, trigger }: RoleEditorProps) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(existing?.name ?? "");
  const [description, setDescription] = useState(existing?.description ?? "");
  const [selected, setSelected] = useState<Set<string>>(
    new Set(existing?.capabilities ?? []),
  );
  const [totp, setTotp] = useState("");

  const create = useCreateCustomRole();
  const update = useUpdateCustomRole();
  const pending = create.isPending || update.isPending;

  const canGrant = (token: string) => bypass || myCaps.includes(token);
  const needsTotp = useMemo(
    () => catalog.some((c) => c.dangerous && selected.has(c.token)),
    [catalog, selected],
  );

  // Group catalog by module for the matrix.
  const byModule = useMemo(() => {
    const m = new Map<string, Capability[]>();
    for (const c of catalog) {
      const arr = m.get(c.module) ?? [];
      arr.push(c);
      m.set(c.module, arr);
    }
    return [...m.entries()];
  }, [catalog]);

  const toggle = (token: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(token)) next.delete(token);
      else next.add(token);
      return next;
    });
  };

  const reset = () => {
    setName(existing?.name ?? "");
    setDescription(existing?.description ?? "");
    setSelected(new Set(existing?.capabilities ?? []));
    setTotp("");
  };

  const onSubmit = async () => {
    const body = {
      name: name.trim(),
      description: description.trim() || undefined,
      capabilities: [...selected],
      totp_code: totp.trim() || undefined,
    };
    try {
      if (existing) await update.mutateAsync({ id: existing.id, body });
      else await create.mutateAsync(body);
      toast.success(existing ? "Role updated." : "Role created.");
      setOpen(false);
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (v) reset();
      }}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{existing ? `Edit ${existing.name}` : "Create Custom Role"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="role-name">Name *</Label>
            <Input
              id="role-name"
              value={name}
              maxLength={100}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Payroll Operator"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="role-desc">Description</Label>
            <Input
              id="role-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What can this role do?"
            />
          </div>

          <div className="space-y-3">
            <Label>Capabilities</Label>
            <p className="text-xs text-muted-foreground">
              Custom roles only ADD permissions on top of a member&apos;s base
              role. You can only grant capabilities you hold yourself.
            </p>
            {/* One collapsed section per module with a "n of m" count, so the
                dialog opens short and Create stays in reach. */}
            <Accordion type="multiple" className="rounded-md border">
              {byModule.map(([moduleName, caps]) => {
                const picked = caps.filter((c) => selected.has(c.token)).length;
                return (
                  <AccordionItem
                    key={moduleName}
                    value={moduleName}
                    className="last:border-b-0"
                  >
                    <AccordionTrigger className="min-h-11 px-3 py-2 text-sm hover:no-underline">
                      <span className="flex min-w-0 flex-1 items-center gap-2 text-left">
                        <span className="truncate font-semibold text-muted-foreground">
                          {capabilityModuleLabel(moduleName)}
                        </span>
                        <span className="ml-auto mr-2 shrink-0 text-xs font-normal text-muted-foreground">
                          {picked} of {caps.length}
                        </span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="px-3 pb-2">
                      <div className="grid gap-x-2 sm:grid-cols-2">
                        {caps.map((c) => {
                          const grantable = canGrant(c.token);
                          return (
                            <label
                              key={c.token}
                              className="flex min-h-11 cursor-pointer items-center gap-2 text-sm has-[:disabled]:cursor-not-allowed"
                              title={grantable ? c.description : "You do not hold this capability"}
                            >
                              <Checkbox
                                checked={selected.has(c.token)}
                                disabled={!grantable}
                                onCheckedChange={() => toggle(c.token)}
                              />
                              <span className={grantable ? "" : "opacity-50"}>
                                {c.label}
                                {c.dangerous && (
                                  <Badge variant="destructive" className="ml-1.5 text-[10px]">
                                    High Risk
                                  </Badge>
                                )}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </div>

          {needsTotp && (
            <div className="space-y-1.5">
              <Label htmlFor="role-totp" className="flex items-center gap-1.5">
                <ShieldAlert className="h-4 w-4 text-destructive" />
                2FA Code (Required for High-Risk Capabilities)
              </Label>
              <Input
                id="role-totp"
                value={totp}
                inputMode="numeric"
                maxLength={6}
                onChange={(e) => setTotp(e.target.value.replace(/\D/g, ""))}
                placeholder="123456"
              />
            </div>
          )}

        </div>
        <DialogFooter className="gap-2 pt-2">
          <Button variant="outline" onClick={() => setOpen(false)} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={pending || name.trim() === ""}>
            {pending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            {existing ? "Save Changes" : "Create Role"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Assign-members dialog (per role) — list current holders + add/remove
// ---------------------------------------------------------------------------

interface RoleMembersDialogProps {
  role: CustomRole;
  /** True when the role carries any `dangerous` capability — server requires a
   *  step-up 2FA code on assign, so we collect one. */
  dangerous: boolean;
  trigger: React.ReactNode;
}

function RoleMembersDialog({ role, dangerous, trigger }: RoleMembersDialogProps) {
  const [open, setOpen] = useState(false);
  const [pickedId, setPickedId] = useState<number | undefined>(undefined);
  const [totp, setTotp] = useState("");

  // Only fetch holders while the dialog is open (lazy, per role).
  const membersQ = useRoleMembers(role.id, open);
  const assign = useAssignCustomRole();
  const unassign = useUnassignCustomRole();

  const members = membersQ.data?.items ?? [];
  const memberIds = useMemo(() => new Set(members.map((m) => m.id)), [members]);

  const reset = () => {
    setPickedId(undefined);
    setTotp("");
  };

  const onAssign = async () => {
    if (pickedId == null) return;
    if (memberIds.has(pickedId)) {
      toast.info("That person already holds this role.");
      return;
    }
    try {
      await assign.mutateAsync({
        id: role.id,
        userId: pickedId,
        totpCode: dangerous ? totp.trim() || undefined : undefined,
      });
      toast.success("Role assigned.");
      reset();
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  };

  const onRemove = async (userId: number) => {
    try {
      await unassign.mutateAsync({ id: role.id, userId });
      toast.success("Role removed.");
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (v) reset();
      }}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Members of {role.name}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Current holders */}
          <div className="space-y-2">
            <Label>Current Holders</Label>
            {membersQ.isLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-9 w-full" />
                <Skeleton className="h-9 w-full" />
              </div>
            ) : membersQ.isError ? (
              <Alert variant="destructive">
                <AlertTitle>Couldn&apos;t load members</AlertTitle>
                <AlertDescription>Please try again in a moment.</AlertDescription>
              </Alert>
            ) : members.length === 0 ? (
              <p className="rounded-md border border-dashed py-6 text-center text-sm text-muted-foreground">
                No one is assigned this role yet.
              </p>
            ) : (
              <ul className="divide-y rounded-md border">
                {members.map((m) => (
                  <li
                    key={m.id}
                    className="flex items-center justify-between gap-3 px-3 py-2"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{m.display_name}</p>
                      <p className="truncate text-xs text-muted-foreground">{m.email}</p>
                    </div>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          disabled={unassign.isPending}
                          aria-label={`Remove ${m.display_name}`}
                        >
                          <UserMinus className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>
                            Remove {m.display_name} from &quot;{role.name}&quot;?
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            They will lose the capabilities this role grants.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => onRemove(m.id)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            Remove
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Add a member */}
          <div className="space-y-2 border-t pt-4">
            <Label>Assign to a Member</Label>
            <EmployeeDirectoryPicker
              value={pickedId}
              onChange={setPickedId}
              disabled={assign.isPending}
              placeholder="Choose someone to grant this role"
            />
            {dangerous && (
              <div className="space-y-1.5 pt-1">
                <Label htmlFor="assign-totp" className="flex items-center gap-1.5">
                  <ShieldAlert className="h-4 w-4 text-destructive" />
                  2FA code (this role carries a high-risk capability)
                </Label>
                <Input
                  id="assign-totp"
                  value={totp}
                  inputMode="numeric"
                  maxLength={6}
                  onChange={(e) => setTotp(e.target.value.replace(/\D/g, ""))}
                  placeholder="123456"
                />
              </div>
            )}
            <div className="flex justify-end pt-1">
              <Button
                size="sm"
                onClick={onAssign}
                disabled={assign.isPending || pickedId == null}
              >
                {assign.isPending && (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                )}
                Assign
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Route component
// ---------------------------------------------------------------------------

function SettingsRolesRoute() {
  const catalogQ = useCapabilityCatalog();
  const rolesQ = useCustomRoles();
  const { caps: myCaps, bypass, has } = useEffectiveCapabilities();
  const del = useDeleteCustomRole();

  const catalog = catalogQ.data?.items ?? [];
  const roles = rolesQ.data?.items ?? [];
  const canAdmin = bypass || has("role.admin");

  // Tokens flagged high-risk in the catalog — a role carrying any of these
  // requires a step-up 2FA code on assign (server enforces; UI collects it).
  const dangerousTokens = useMemo(
    () => new Set(catalog.filter((c) => c.dangerous).map((c) => c.token)),
    [catalog],
  );
  const roleIsDangerous = (r: CustomRole) =>
    r.capabilities.some((c) => dangerousTokens.has(c));

  const onDelete = async (r: CustomRole) => {
    try {
      await del.mutateAsync(r.id);
      toast.success("Role deleted.");
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  };

  return (
    <>
      <SettingsPageHeader />
      <SettingsShell groups={SETTINGS_GROUPS}>
        <div className="space-y-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold">Custom Roles</h2>
              <p className="text-sm text-muted-foreground">
                Define narrow roles by granting specific capabilities. Custom
                roles add to a member&apos;s base role; they never remove access.
              </p>
            </div>
            {canAdmin && (
              <RoleEditor
                catalog={catalog}
                myCaps={myCaps}
                bypass={bypass}
                trigger={
                  <Button size="sm" className="gap-1.5">
                    <PlusCircle className="h-4 w-4" />
                    New Role
                  </Button>
                }
              />
            )}
          </div>

          {!canAdmin && (
            <Alert>
              <AlertTitle>Restricted</AlertTitle>
              <AlertDescription>
                You need the role administration capability to manage custom roles.
              </AlertDescription>
            </Alert>
          )}

          {(catalogQ.isLoading || rolesQ.isLoading) && (
            <div className="space-y-3">
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          )}

          {rolesQ.data && roles.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center text-sm text-muted-foreground">
                No custom roles yet.
              </CardContent>
            </Card>
          )}

          <div className="space-y-3">
            {roles.map((r) => (
              <Card key={r.id}>
                <CardHeader>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <CardTitle className="text-base">{r.name}</CardTitle>
                      {r.description && (
                        <p className="mt-1 text-xs text-muted-foreground">{r.description}</p>
                      )}
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {r.capabilities.length === 0 && (
                          <span className="text-xs text-muted-foreground">No capabilities</span>
                        )}
                        {r.capabilities.map((c) => (
                          <Badge key={c} variant="secondary" className="font-mono text-[10px]">
                            {c}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    {canAdmin && (
                      <div className="flex flex-wrap items-center gap-2">
                        <RoleMembersDialog
                          role={r}
                          dangerous={roleIsDangerous(r)}
                          trigger={
                            <Button variant="outline" size="sm" className="gap-1.5">
                              <Users className="h-3.5 w-3.5" />
                              Assign Members
                            </Button>
                          }
                        />
                        <RoleEditor
                          catalog={catalog}
                          myCaps={myCaps}
                          bypass={bypass}
                          existing={r}
                          trigger={
                            <Button variant="outline" size="sm">
                              Edit
                            </Button>
                          }
                        />
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                Delete Role &quot;{r.name}&quot;?
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                Members will lose its capabilities. This cannot
                                be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => onDelete(r)}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    )}
                  </div>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </SettingsShell>
    </>
  );
}
